import { useCallback, useEffect, useState } from 'react'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { api } from '../lib/api'
import { useAuth } from '../contexts/AuthContext'
import { consumeSignInReturn } from '../lib/postSignInReturn'
import { usePageTitle } from '../hooks/usePageTitle'
import {
  ClubButton,
  ClubLinkButton,
  Container,
  Eyebrow,
  JoinPage,
} from '../components/golf'

/**
 * Sign-in confirmation (`/join/confirm`) — the interstitial the emailed link
 * lands on.
 *
 * The email's GET deliberately signs nobody in. It only checks the token is
 * live and sends the browser here; this screen names the identity and completes
 * the sign-in with a CSRF-protected POST.
 *
 * That exists for two reasons, both real:
 *
 *   * Login-CSRF. Previously an attacker could request a link for their OWN
 *     account, forward it, and whoever clicked was silently signed into the
 *     attacker's account — SameSite=Lax does not block a top-level GET. Naming
 *     the address is what turns a silent hijack into something the reader can
 *     refuse.
 *   * Link prefetching. Mail scanners and Slack unfurls issue GETs. When the GET
 *     consumed the token, the link was already dead by the time the human
 *     clicked, with no visible cause.
 *
 * A same-device nonce cookie would also have closed the CSRF hole, but it breaks
 * requesting on a laptop and opening on a phone. This keeps that working.
 *
 * No step numeral. This used to claim "II", which /join/checkout also claims —
 * the flow read I, II, II, III. Signing in is not step n of an application
 * anyway: a returning member reaches this screen having never seen a step I.
 */
export function JoinConfirmScreen() {
  usePageTitle('Confirm sign-in')
  const navigate = useNavigate()
  const { refreshSession } = useAuth()
  const search = useSearch({ strict: false }) as { token?: string }
  const token = search.token ?? ''

  const [email, setEmail] = useState<string | null>(null)
  const [lookupFailed, setLookupFailed] = useState(false)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // A URL with no token at all is derived, not stateful — setting state for it
  // inside the effect below would just trigger a cascading render.
  const deadLink = !token || lookupFailed

  // Ask who this token belongs to. Does not consume it, so landing here twice
  // (or having the page prefetched) costs nothing.
  useEffect(() => {
    if (!token) return
    let active = true
    api
      .get<{ email: string }>(`/auth/magic_link/preview?token=${encodeURIComponent(token)}`)
      .then((data) => {
        if (active) setEmail(data.email)
      })
      .catch(() => {
        if (active) setLookupFailed(true)
      })
    return () => {
      active = false
    }
  }, [token])

  const confirm = useCallback(async () => {
    setSubmitting(true)
    setError(null)
    try {
      const data = await api.post<{ redirect_to: string }>('/auth/verify', { token })
      await refreshSession()
      // A tokened invite page may have recorded itself as this sign-in's
      // errand (see postSignInReturn) — a member who signed in mid-acceptance
      // goes back to finish it, everyone else where the server says.
      navigate({ to: consumeSignInReturn() ?? (data.redirect_to || '/clubhouse') })
    } catch (err) {
      // A token can die between the preview and the click (15-minute TTL, and
      // single-use), so this is an expected path, not just a server fault.
      setLookupFailed(true)
      setError(err instanceof Error ? err.message : 'This sign-in link is no longer valid.')
      setSubmitting(false)
    }
  }, [token, refreshSession, navigate])

  if (deadLink) {
    return (
      <JoinPage stepLabel="Sign in">
        <Container className="max-w-[620px] px-0 text-center">
          <Eyebrow>This link has expired</Eyebrow>
          <h1 className="mb-3.5 mt-4.5 font-display text-[clamp(2rem,4.5vw,2.75rem)] font-medium leading-[1.06] tracking-[-0.014em] text-pine">
            That link is no longer good.
          </h1>
          <p className="mx-auto max-w-[46ch] text-[15.5px] text-ink-soft">
            {error ??
              'Sign-in links last fifteen minutes and work once. Ask for a fresh one and it will be posted straight away.'}
          </p>
          <div className="mt-9 flex flex-wrap justify-center gap-3.5">
            {/*
              Must match AuthController::INVALID_LINK_PATH. This is the same
              dead link detected client-side; if the two disagree, one expired
              link lands on the sign-in door and another on the application.
            */}
            <ClubLinkButton to="/sign-in">Send me a new link</ClubLinkButton>
            <ClubLinkButton to="/clubhouse" variant="ghost">
              Look around first
            </ClubLinkButton>
          </div>
        </Container>
      </JoinPage>
    )
  }

  return (
    <JoinPage stepLabel="Sign in">
      <Container className="max-w-[620px] px-0 text-center">
        <Eyebrow>Confirm your sign-in</Eyebrow>
        <h1 className="mb-3.5 mt-4.5 font-display text-[clamp(2rem,4.5vw,2.75rem)] font-medium leading-[1.06] tracking-[-0.014em] text-pine">
          Sign in to the club?
        </h1>

        {/* The address is the whole point of this screen: a reader who does not
            recognise it should stop here rather than be signed into it. */}
        <p className="mx-auto max-w-[46ch] text-[15.5px] text-ink-soft">
          You are about to sign in as{' '}
          {email ? (
            <strong className="font-medium text-pine">{email}</strong>
          ) : (
            <span className="text-ink-soft/70">…</span>
          )}
          .
        </p>

        <div className="mt-9 flex flex-wrap justify-center gap-3.5">
          <ClubButton onClick={confirm} disabled={submitting || !email}>
            {submitting ? 'Signing you in…' : 'Sign me in'}
          </ClubButton>
        </div>

        <p className="mt-7 text-[13.5px] text-ink-soft">
          Not your address?{' '}
          <span className="text-pine">
            Close this page and discard the link — nothing has been signed in yet.
          </span>
        </p>
      </Container>
    </JoinPage>
  )
}
